import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchProductById } from '../services/api';
import { formatPrice } from '../utils/formatPrice';
import LoadingSpinner from '../components/LoadingSpinner';
import ErrorMessage from '../components/ErrorMessage';
import Layout from '../components/Layout';

const ProductDetailPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedImageIndex, setSelectedImageIndex] = useState(0);
    const [images, setImages] = useState([]);

    useEffect(() => {
        const loadProduct = async () => {
            try {
                setLoading(true);
                setError(null);
                const response = await fetchProductById(id);
                // Backend returns { status: 'success', data: product }
                // API service returns response.data, so we get { status: 'success', data: product }
                const productData = response.data || response;
                if (!productData) {
                    throw new Error('Product data not found in response');
                }
                setProduct(productData);
                
                // Use images array if available, fallback to imageUrl for backward compatibility
                // Filter out empty, white, or placeholder images
                let imageList = [];
                if (productData.images && productData.images.length > 0) {
                    imageList = productData.images;
                } else if (productData.imageUrl) {
                    // Fallback to single imageUrl for existing products
                    imageList = [productData.imageUrl];
                }
                
                // Filter out invalid images (empty, white, placeholder, gif)
                const validImages = imageList.filter(img => {
                    if (!img || typeof img !== 'string') return false;
                    const url = img.toLowerCase();
                    // Filter out placeholders, white images, empty URLs, and GIF files
                    return !url.includes('placeholder') && 
                           !url.includes('white') && 
                           !url.includes('blank') &&
                           !url.includes('no-image') &&
                           !url.endsWith('.gif') &&
                           !url.includes('.gif?') &&
                           url.trim().length > 0;
                });
                
                setImages(validImages);
            } catch (err) {
                setError(err.message || 'Failed to load product');
            } finally {
                setLoading(false);
            }
        };

        if (id) {
            loadProduct();
        }
    }, [id]);

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <LoadingSpinner />
            </div>
        );
    }

    if (error || !product) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <ErrorMessage message={error || 'Product not found'} />
            </div>
        );
    }

    const discountPercent = product.mrp > 0 && product.super_price > 0
        ? Math.round(((product.mrp - product.super_price) / product.mrp) * 100)
        : product.discount || 0;

    return (
        <Layout>
            <div className="min-h-screen bg-gray-50 py-8">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    {/* Back Button */}
                    <button
                        onClick={() => navigate(-1)}
                        className="mb-6 flex items-center text-gray-600 hover:text-gray-900 transition-colors"
                    >
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                        Back
                    </button>

                    <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                        <div className="grid md:grid-cols-2 gap-8 p-6 lg:p-10">
                        {/* Image Gallery Section - Left Side */}
                        <div className="space-y-4">
                            {/* Main Image */}
                            <div className="relative aspect-square bg-gray-100 rounded-lg overflow-hidden">
                                {images.length > 0 && images[selectedImageIndex] ? (
                                    <img
                                        src={images[selectedImageIndex]}
                                        alt={product.name}
                                        className="w-full h-full object-contain"
                                        onError={(e) => {
                                            e.target.style.display = 'none';
                                        }}
                                    />
                                ) : (
                                    <div className="w-full h-full flex flex-col items-center justify-center bg-gray-200">
                                        <svg 
                                            className="w-24 h-24 text-gray-400 mb-4" 
                                            fill="none" 
                                            viewBox="0 0 24 24" 
                                            stroke="currentColor"
                                        >
                                            <path 
                                                strokeLinecap="round" 
                                                strokeLinejoin="round" 
                                                strokeWidth={2} 
                                                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" 
                                            />
                                        </svg>
                                        <p className="text-gray-500">No Image Available</p>
                                    </div>
                                )}
                            </div>

                            {/* Thumbnail Gallery - Only show if multiple images */}
                            {images.length > 1 && (
                                <div className="flex gap-2 overflow-x-auto pb-2">
                                    {images.map((image, index) => (
                                        <button
                                            key={index}
                                            onClick={() => setSelectedImageIndex(index)}
                                            className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all ${
                                                selectedImageIndex === index
                                                    ? 'border-blue-500 ring-2 ring-blue-200'
                                                    : 'border-gray-200 hover:border-gray-300'
                                            }`}
                                        >
                                            <img
                                                src={image}
                                                alt={`${product.name} - View ${index + 1}`}
                                                className="w-full h-full object-cover"
                                                onError={(e) => {
                                                    e.target.style.display = 'none';
                                                }}
                                            />
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Product Details Section - Right Side */}
                        <div className="space-y-6">
                            {/* Product Name */}
                            <div>
                                <h1 className="text-3xl font-bold text-gray-900 mb-2">
                                    {product.name}
                                </h1>
                                <p className="text-sm text-gray-500">
                                    {product.category}
                                </p>
                            </div>

                            {/* Price Section */}
                            <div className="space-y-2">
                                <div className="flex items-baseline gap-3">
                                    <span className="text-4xl font-bold text-gray-900">
                                        {formatPrice(product.super_price)}
                                    </span>
                                    {product.mrp > product.super_price && (
                                        <span className="text-2xl text-gray-500 line-through">
                                            {formatPrice(product.mrp)}
                                        </span>
                                    )}
                                </div>
                                {discountPercent > 0 && (
                                    <div className="flex items-center gap-2">
                                        <span className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm font-semibold">
                                            {discountPercent}% OFF
                                        </span>
                                        <span className="text-sm text-gray-600">
                                            You save {formatPrice(product.mrp - product.super_price)}
                                        </span>
                                    </div>
                                )}
                            </div>

                            {/* Description */}
                            <div>
                                <h2 className="text-lg font-semibold text-gray-900 mb-2">
                                    Description
                                </h2>
                                <p className="text-gray-700 leading-relaxed">
                                    {product.description}
                                </p>
                            </div>

                            {/* Tags */}
                            {product.tags && product.tags.length > 0 && (
                                <div>
                                    <h2 className="text-lg font-semibold text-gray-900 mb-2">
                                        Tags
                                    </h2>
                                    <div className="flex flex-wrap gap-2">
                                        {product.tags.map((tag, index) => (
                                            <span
                                                key={index}
                                                className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm"
                                            >
                                                {tag}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default ProductDetailPage;

